import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Navigate, useParams } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { Box, GlobalStyles } from '@mui/material'
import { useSnackbar } from 'notistack'
import { Navbar } from '../../component/shared/Navbar'
import ErrorMessage from '../../component/shared/component/ErrorMessage'
import Stage from './Stage'
import StageHeader from './StageHeader'
import TicketWall from './TicketWall'
import WinnerConfetti from './winner/WinnerConfetti'
import WinnerPopout from './winner/WinnerPopout'
import WinnersBoard from './winner/WinnersBoard'
import { useFullscreen } from './useFullscreen'
import { NO_BACKGROUND_REFETCH, useWall } from './useWall'
import { playFromStart } from './sound'
import { extractErrorMessage } from '../../utils/extractErrorMessage'
import { MANUAL_SOURCE } from '../../utils/luckyDraw'
import { useGetLuckyDraw, useRollLuckyDraw } from '../../api/lucky-draw'

// Page-scoped, so every rule lifts when the operator leaves.
const STAGE_GLOBAL_STYLES = {
  // The support widget is injected outside React's tree, so CSS is the only lever.
  '#jsd-widget, iframe[id*="jsd"], iframe[src*="atlassian.com"]': { display: 'none !important' },
  // Both elements: on body alone the vertical axis computes to auto and gains its own scrollbar.
  'html, body': { overflowX: 'hidden' },
  // Body only — html propagates it to the canvas, and leaves the native scrollbar alone.
  body: { backgroundColor: '#07070d' },
}

// Matches EXHAUSTED in server/services/lucky-draw.roll.service.js: a cap-caused refusal, which the
// projector never shows.
const POOL_EXHAUSTED = 'POOL_EXHAUSTED'
// Matches LIST_CHANGED in server/controllers/lucky-draw.roll.controller.js: the entry list moved
// after this wall loaded. Quiet too, since the room need not hear about a ticket edit.
const LIST_CHANGED = 'LIST_CHANGED'
const QUIET_REFUSALS = [POOL_EXHAUSTED, LIST_CHANGED]

// A beat on the lit cards before the dialog covers the wall.
const WINNER_HOLD_MS = 500

// Saving rests rather than going dark: the winners are not in state until the server answers, so
// the landed cards would go out before the popout arrives.
const WALL_MODE = {
  sweeping: 'sweep',
  landing: 'land',
  saving: 'rest',
  revealed: 'rest',
  idle: 'off',
}

export const LuckyDrawRollPage = () => {
  const { id } = useParams()
  const [stopIndex, setStopIndex] = useState(0)
  const [phase, setPhase] = useState('idle')
  // Winners landed since START. Distinct from draw.winners, which is everything recorded.
  const [revealed, setRevealed] = useState([])
  // Opens itself on a draw; WINNERS in the header brings it back without redrawing.
  const [isShowingWinners, setIsShowingWinners] = useState(false)
  // On mount, not module scope: the page is imported eagerly, so every portal page would fetch them.
  const [sweepSound] = useState(() => {
    const audio = new Audio('/sounds/sweep.mp3')
    audio.loop = true

    return audio
  })
  const [winSound] = useState(() => new Audio('/sounds/win.mp3'))

  const { enqueueSnackbar } = useSnackbar()
  const { isFullscreen, toggle: toggleFullscreen } = useFullscreen(() =>
    enqueueSnackbar('The browser blocked full screen. Use F11 instead.', { variant: 'warning' }),
  )

  const {
    data: draw,
    error,
    isFetching: isFetchingDraw,
    refetch: refetchDraw,
  } = useGetLuckyDraw(id, NO_BACKGROUND_REFETCH)
  const { mutateAsync: roll } = useRollLuckyDraw(id)

  const prizes = draw?.prizes ?? []
  const prize = prizes[stopIndex]
  const prizeId = prize?.id ?? ''
  // Batch size, capped by what the prize has left: set to 10 with 3 units left, START takes 3.
  const count = Math.min(prize?.winnersPerDraw ?? 0, prize?.remaining ?? 0)
  // Held: filtered inline it was a new array per render, so START re-rendered every winner row.
  const prizeWinners = useMemo(
    () => (draw?.winners ?? []).filter((winner) => winner.prizeId === prizeId),
    [draw?.winners, prizeId],
  )
  // A manual draw's pool is the uploaded sheet, so without one there is nothing to draw from.
  const needsEntryList = draw?.source === MANUAL_SOURCE && draw.entryCount === 0

  const {
    cards,
    headroom,
    won,
    wonTickets,
    undrawn,
    live,
    isExhausted,
    cap,
    poolVersion,
    isLoading: isLoadingPool,
    isFetching: isFetchingPool,
    error: poolError,
    refetch: refetchPool,
  } = useWall(id, prizeId, count)

  // The header's buttons are gated mid-draw; a browser back is not, so an award can land after
  // the page has gone.
  const isMounted = useRef(true)
  // A ref, not a dependency: endQuietly feeds onLanded, and a new onLanded mid-sweep restarts it.
  const fullscreen = useRef(isFullscreen)

  useEffect(() => {
    fullscreen.current = isFullscreen
  }, [isFullscreen])

  useEffect(
    () => () => {
      isMounted.current = false
    },
    [],
  )

  // A different prize is a different wall. The pool needs no reset: its key holds the prize.
  useEffect(() => {
    setRevealed([])
    setPhase('idle')
  }, [prizeId])

  // Resting holds what is lit rather than re-deriving it, so an emptied list has to close itself.
  useEffect(() => {
    if (phase === 'revealed' && revealed.length === 0) setPhase('idle')
  }, [phase, revealed.length])

  const refetchBoth = useCallback(
    () => Promise.all([refetchDraw(), refetchPool()]),
    [refetchDraw, refetchPool],
  )

  /** End a press, naming the reason only where a room cannot read it. */
  const endQuietly = useCallback(
    async (reason) => {
      setPhase('idle')

      if (!fullscreen.current) enqueueSnackbar(reason, { variant: 'info' })

      await refetchBoth()
    },
    [enqueueSnackbar, refetchBoth],
  )

  const isDrawing = phase === 'sweeping' || phase === 'landing' || phase === 'saving'

  // Keyed on isDrawing, not phase: STOP must not restart the roll. The crash cuts it at the stop;
  // this cleanup covers every other way out (a dry pool, leaving the page).
  useEffect(() => {
    if (!isDrawing) return undefined

    playFromStart(sweepSound)

    return () => sweepSound.pause()
  }, [isDrawing, sweepSound])

  // isFetching, not isLoading: a stale count would let a fast second press ask for more units than
  // remain. poolError, because a failed refetch leaves the cache holding pre-award data. live,
  // because the wall runs on that list, so the button and the draw cannot disagree.
  const canStart =
    !isDrawing && !isFetchingDraw && !isFetchingPool && !poolError && live.length > 0 && count >= 1

  const handleStart = () => {
    setIsShowingWinners(false)
    // Only this press's winners stay lit: leaving earlier cards gold would show the room which
    // ones the spotlight no longer visits, which is the cap by another name.
    setRevealed([])
    setPhase('sweeping')
  }

  const handleStop = () => setPhase('landing')

  // On the spotlight's last flash, not after the save: the room hears the crash as the wall stops.
  // Stable, like onLanded, since the wall's animation effect depends on it.
  const onStopped = useCallback(() => {
    sweepSound.pause()
    playFromStart(winSound)
  }, [sweepSound, winSound])

  // Kept stable on purpose: it is a dependency of the wall's animation effect, and changing it
  // mid-sweep would restart the sweep.
  const onLanded = useCallback(
    async (landed) => {
      // Nothing on the wall can win. Quietly, because the caps are internal.
      if (landed.length === 0) {
        await endQuietly('No ticket left that can win — every company is at its cap.')

        return
      }

      setPhase('saving')

      // Short press. Off the projection only: the cause is always a cap.
      if (landed.length < count && !fullscreen.current) {
        enqueueSnackbar(`Only ${landed.length} of ${count} could be drawn — the pool ran out.`, {
          variant: 'warning',
        })
      }

      try {
        const tickets = landed.map((card) => card.ticket)
        const { data } = await roll({ prizeId, tickets, poolVersion })
        if (!isMounted.current) return

        // Unbatched in React 17, but both phases map the wall to 'rest', so the second write
        // changes no dependency and the extra commit costs one memoised render.
        setRevealed(data.winners)
        setPhase('revealed')

        // Started before the hold, not after: the two are independent, and the round trip is dead
        // time on a dark START button otherwise.
        const settled = refetchBoth()

        await new Promise((resolve) => setTimeout(resolve, WINNER_HOLD_MS))
        if (!isMounted.current) return

        setIsShowingWinners(true)

        // Awaited all the same: START is gated on the pool, and lighting it early sends a press
        // against a pool the server has already emptied.
        await settled
      } catch (e) {
        if (!isMounted.current) return

        // Cap-caused or stale-list ends quietly. Anything else is the operator's to see — nothing
        // was recorded, since the server checks the whole batch before writing.
        if (QUIET_REFUSALS.includes(e.response?.data?.code)) {
          await endQuietly(extractErrorMessage(e))

          return
        }

        setPhase('idle')
        enqueueSnackbar(extractErrorMessage(e), { variant: 'error' })

        // Refetched though nothing was recorded: a refusal usually means stale local counts, and
        // neither query self-corrects, so every later press would repeat the same error.
        await refetchBoth()
      }
    },
    [roll, prizeId, poolVersion, count, enqueueSnackbar, endQuietly, refetchBoth],
  )

  const goToStop = (index) => {
    setStopIndex(index)
    setPhase('idle')
    setRevealed([])
    setIsShowingWinners(false)
  }

  /**
   * Removing a winner takes its card off the wall too: the wall lights whatever `revealed` holds,
   * so dropping it from there is the whole job. Stable, or the board rebuilds on every phase.
   */
  const onWinnersRemoved = useCallback(
    (removedIds) => {
      setRevealed((shown) => shown.filter((winner) => !removedIds.includes(winner.id)))
      refetchBoth()
    },
    [refetchBoth],
  )

  // Held, so START does not rebuild a row per winner in the commit that starts the spotlight.
  const board = useMemo(
    () => (
      <WinnersBoard drawId={id} prize={prize} winners={prizeWinners} onRemoved={onWinnersRemoved} />
    ),
    [id, prize, prizeWinners, onWinnersRemoved],
  )

  // Nothing to draw for, so send the operator where prizes are defined.
  if (draw && prizes.length === 0) {
    return <Navigate to={`/lucky-draw/${id}/edit`} replace />
  }

  const wallMode = WALL_MODE[phase]

  return (
    <>
      <Helmet titleTemplate='%s | Staff Portal' defaultTitle='Staff Portal'>
        <title>{draw?.name || 'Lucky Draw'}</title>
      </Helmet>
      <GlobalStyles styles={STAGE_GLOBAL_STYLES} />
      {!isFullscreen && <Navbar />}
      <Box
        sx={{
          minHeight: isFullscreen ? '100vh' : 0,
          overflowX: 'hidden',
          color: '#fff',
          backgroundColor: '#07070d',
          backgroundImage:
            'radial-gradient(circle at 50% -10%, rgba(247, 201, 72, 0.18), transparent 55%)',
        }}
      >
        {/* Phase-gated: the last card lands before the batch is finished. */}
        <WinnerConfetti winners={phase === 'revealed' ? revealed : []} />
        <Box
          display='flex'
          flexDirection='column'
          gap={{ xs: 1.5, md: 2 }}
          px={{ xs: 2, md: 4 }}
          py={{ xs: 1.5, md: 2 }}
          sx={{
            // No global box-sizing reset in this app, so the padding would otherwise add to the
            // height and push the bottom row of cards off screen.
            boxSizing: 'border-box',
            // Header and wall share one viewport rather than each claiming their own.
            height: isFullscreen ? '100vh' : 'auto',
          }}
        >
          <StageHeader
            stopIndex={stopIndex}
            prizeCount={prizes.length}
            isRolling={isDrawing}
            isFullscreen={isFullscreen}
            canShowWinners={revealed.length > 0 && !isShowingWinners && !isDrawing}
            onShowWinners={() => setIsShowingWinners(true)}
            onPrev={() => goToStop(stopIndex - 1)}
            onNext={() => goToStop(stopIndex + 1)}
            onToggleFullscreen={toggleFullscreen}
          />

          {error ? (
            <ErrorMessage error={error} onRetry={refetchDraw} />
          ) : (
            <Box
              sx={{
                // Fullscreen: takes what the header leaves. Outside it the column has no height to
                // divide, and a flex-basis of 0 would collapse the wall, so it states its own.
                flex: isFullscreen ? '1 1 0' : 'none',
                height: isFullscreen ? 'auto' : '72vh',
                minHeight: isFullscreen ? 0 : 420,
              }}
            >
              <Stage
                prize={prize}
                ticketCount={cards.length}
                slots={count}
                wall={
                  cards.length > 0 && (
                    <TicketWall
                      cards={cards}
                      mode={wallMode}
                      slots={count}
                      undrawn={undrawn}
                      live={live}
                      headroom={headroom}
                      won={won}
                      wonTickets={wonTickets}
                      winners={revealed}
                      onStopped={onStopped}
                      onLanded={onLanded}
                    />
                  )
                }
                winners={revealed}
                isSweeping={phase === 'sweeping'}
                canStart={canStart}
                onStart={handleStart}
                onStop={handleStop}
                needsEntryList={needsEntryList}
                isExhausted={isExhausted}
                cap={cap}
                isFullscreen={isFullscreen}
                isLoadingPool={isLoadingPool}
                poolError={poolError}
                onRetryPool={refetchPool}
              />
            </Box>
          )}

          {/* Never open on nothing: it is raised from a timer after the winners land, and stepping
              to another prize in that window empties the list under it. */}
          <WinnerPopout
            winners={revealed}
            open={isShowingWinners && revealed.length > 0}
            onClose={() => setIsShowingWinners(false)}
          />
        </Box>

        {/* Outside the viewport-high block: below the fold, where a scroll reaches it and the
            projection does not. */}
        {board}
      </Box>
    </>
  )
}
